import React, { useState, useCallback, ChangeEvent, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  MdSearch, MdPerson, MdPrint, MdAccountBalance,
  MdDownload, MdReceipt, MdPayments, MdLocalShipping,
} from 'react-icons/md';
import {
  PageHeader, Card, Field, Input, Button, Spinner, Pagination,
} from '../../components/common';
import { clientAPI } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import type { Client, ClientStatement } from '../../types';

// ─── Statement Table ──────────────────────────────────────────────────────────
function StatementTable({
  statement, client, fromDate, toDate, onPage,
}: {
  statement: ClientStatement;
  client:    Client;
  fromDate:  string;
  toDate:    string;
  onPage:    (list: 'contracts' | 'directSales', page: number) => void;
}) {
  const printRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const params: { from?: string; to?: string } = {};
      if (fromDate) params.from = fromDate;
      if (toDate)   params.to   = toDate;
      const res = await clientAPI.getStatementPDF(client._id, params);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = /filename="?([^"]+)"?/.exec((res.headers?.['content-disposition'] as string) || '')?.[1] || `Statement-${client.name.replace(/\s+/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloading(false); }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head>
        <title>Statement — ${client.name}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 11px; color: #1a1a2e; padding: 16px; }
          h1 { font-size: 18px; font-weight: 700; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th { padding: 7px; text-align: left; font-size: 10px; text-transform: uppercase; background:#3D52A0; color:#fff; }
          td { padding: 7px; font-size: 11px; border: 1px solid #eee; vertical-align: middle; }
          .right { text-align: right; }
          .center { text-align: center; }
          @page { size: A4 landscape; margin: 8mm; }
        </style>
      </head><body>${printRef.current.innerHTML}</body></html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

    const contracts           = statement?.contracts ?? [];
  const totalContractsValue = statement?.totalContractsValue ?? 0;
  const totalPayments       = statement?.totalPayments ?? 0;
  const totalDespatched     = statement?.totalDespatched ?? 0;
  const outstanding         = statement?.outstanding ?? 0;
  const directSales            = statement?.directSales ?? [];
  const directSalesValue       = statement?.directSalesValue ?? 0;
  const directSalesPaid        = statement?.directSalesPaid ?? 0;
  const directSalesOutstanding = statement?.directSalesOutstanding ?? 0;
  // Rows are paged (10 per list); counts and totals cover the whole statement.
  const contractPage = statement?.contractsPagination, salePage = statement?.directSalesPagination;
  const contractCount = contractPage?.total ?? contracts.length, saleCount = salePage?.total ?? directSales.length;
  const contractOffset = contractPage ? (contractPage.page - 1) * contractPage.limit : 0;
  const saleOffset = salePage ? (salePage.page - 1) * salePage.limit : 0;

  // Bail out gracefully if response shape is wrong (e.g. backend not restarted)
  if (!Array.isArray(statement?.contracts)) {
    return (
      <Card className="p-8 text-center">
        <div className="text-sm text-ink-3 font-semibold mb-1">Statement format outdated</div>
        <div className="text-xs text-ink-4">
          The server returned an unexpected response. Please restart the backend or contact support.
        </div>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-ink-5" style={{ background: 'var(--p-pale)' }}>
        <span className="text-xs font-bold uppercase tracking-widest text-ink-3">
          Account Statement — {contractCount} contract(s)
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={handleDownloadPDF} loading={downloading} icon={<MdDownload size={14} />}>
            Download PDF
          </Button>
          <Button size="sm" variant="secondary" onClick={handlePrint} icon={<MdPrint size={14} />}>
            Print
          </Button>
        </div>
      </div>

      {/* Printable Area */}
      <div ref={printRef} className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-5 pb-4 border-b border-ink-5">
          <div>
            <h1 className="text-xl font-bold text-ink">Account Statement</h1>
            <p className="text-sm text-ink-4 mt-0.5">
              {fromDate && toDate ? `Period: ${formatDate(fromDate)} — ${formatDate(toDate)}` : 'All Transactions'}
            </p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end mb-1">
              <MdPerson size={16} className="text-p" />
              <span className="text-base font-bold text-ink">{client.name}</span>
            </div>
            {client.companyName  && <p className="text-sm text-ink-3">{client.companyName}</p>}
            {client.contactNumber && <p className="text-xs text-ink-4 mt-0.5">{client.contactNumber}</p>}
            {client.address && <p className="text-xs text-ink-4">{client.address}</p>}
          </div>
        </div>

        {/* Summary Boxes — 4 boxes */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          <div className="border border-blue-100 bg-blue-50 rounded-sm p-3 text-center" style={{ borderRadius: 'var(--r-xs)' }}>
            <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
              <MdReceipt size={12} className="inline mr-0.5" />Total Contracts Value
            </div>
            <div className="text-lg font-bold text-blue-700">{formatCurrency(totalContractsValue)}</div>
            <div className="text-xs text-blue-500 mt-0.5">{contractCount} contract(s)</div>
          </div>
          <div className="border border-green-100 bg-green-50 rounded-sm p-3 text-center" style={{ borderRadius: 'var(--r-xs)' }}>
            <div className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">
              <MdPayments size={12} className="inline mr-0.5" />Total Payments
            </div>
            <div className="text-lg font-bold text-green-700">{formatCurrency(totalPayments)}</div>
            <div className="text-xs text-green-500 mt-0.5">Cash received</div>
          </div>
          <div className="border border-purple-100 bg-purple-50 rounded-sm p-3 text-center" style={{ borderRadius: 'var(--r-xs)' }}>
            <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-1">
              <MdLocalShipping size={12} className="inline mr-0.5" />Total Despatched
            </div>
            <div className="text-lg font-bold text-purple-700">{formatCurrency(totalDespatched)}</div>
            <div className="text-xs text-purple-500 mt-0.5">Goods delivered</div>
          </div>
          <div className={`border rounded-sm p-3 text-center ${outstanding > 0 ? 'border-red-100 bg-red-50' : 'border-green-100 bg-green-50'}`} style={{ borderRadius: 'var(--r-xs)' }}>
            <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
              <MdAccountBalance size={12} className="inline mr-0.5" />Outstanding
            </div>
            <div className={`text-lg font-bold ${outstanding > 0 ? 'text-red-700' : 'text-green-700'}`}>
              {formatCurrency(Math.abs(outstanding))}
            </div>
            <div className={`text-xs mt-0.5 ${outstanding > 0 ? 'text-red-500' : 'text-green-500'}`}>
              {outstanding > 0 ? 'Receivable' : outstanding < 0 ? 'Overpaid' : 'Settled ✓'}
            </div>
          </div>
        </div>

        {/* Contract Table */}
        {contracts.length === 0 ? (
          <div className="text-center py-10 text-ink-4 text-sm">No contracts found for this period</div>
        ) : (
          <div className="border border-ink-5 rounded-sm overflow-hidden" style={{ borderRadius: 'var(--r-xs)' }}>
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: 'var(--p-pale)' }}>
                  <th className="py-2.5 px-3 text-center text-xs font-semibold text-ink-3 w-10 border border-ink-5">#</th>
                  <th className="py-2.5 px-3 text-left text-xs font-semibold text-ink-3 border border-ink-5">Reference</th>
                  <th className="py-2.5 px-3 text-left text-xs font-semibold text-ink-3 border border-ink-5">Description</th>
                  <th className="py-2.5 px-3 text-right text-xs font-semibold text-blue-600 border border-ink-5">Total Contract</th>
                  <th className="py-2.5 px-3 text-right text-xs font-semibold text-green-700 border border-ink-5">Payment Received</th>
                  <th className="py-2.5 px-3 text-right text-xs font-semibold text-purple-700 border border-ink-5">Despatch Items Price</th>
                  <th className="py-2.5 px-3 text-right text-xs font-semibold text-ink-3 border border-ink-5">Balance</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c, idx) => {
                  const numRows = Math.max(c.payments.length, c.despatches.length, 1);
                  const balColor = c.balance > 0 ? 'text-red-600' : c.balance < 0 ? 'text-green-600' : 'text-ink-4';
                  const balLabel = c.balance > 0 ? 'Receivable' : c.balance < 0 ? 'Overpaid' : 'Settled';

                  return Array.from({ length: numRows }).map((_, r) => {
                    const pay  = c.payments[r];
                    const desp = c.despatches[r];
                    const isFirst = r === 0;
                    const groupBorder = isFirst && idx > 0 ? 'border-t-2 border-t-p/30' : '';

                    return (
                      <tr key={`${c.orderNumber}-${r}`} className={`${groupBorder} ${idx % 2 === 0 ? 'bg-white' : 'bg-ink-5/5'}`}>
                        {isFirst && (
                          <td rowSpan={numRows} className="py-2.5 px-3 text-center text-sm font-bold text-ink-3 border border-ink-5 align-middle bg-p-pale/40">
                            {contractOffset + idx + 1}
                          </td>
                        )}
                        {isFirst && (
                          <td rowSpan={numRows} className="py-2.5 px-3 border border-ink-5 align-middle">
                            <span className="font-mono text-xs font-bold text-p-deep">{c.orderNumber}</span>
                          </td>
                        )}
                        {isFirst && (
                          <td rowSpan={numRows} className="py-2.5 px-3 text-sm text-ink border border-ink-5 align-middle">
                            {c.description}
                          </td>
                        )}
                        {isFirst && (
                          <td rowSpan={numRows} className="py-2.5 px-3 text-right border border-ink-5 align-middle bg-blue-50/50">
                            <span className="text-sm font-bold text-blue-700">{formatCurrency(c.contractTotal)}</span>
                          </td>
                        )}
                        <td className="py-2 px-3 text-right border border-ink-5 align-middle">
                          {pay ? (
                            <div>
                              <div className="text-sm font-semibold text-green-700">{formatCurrency(pay.amount)}</div>
                              <div className="text-xs text-ink-4 mt-0.5">{pay.label} · {formatDate(pay.date)}</div>
                              {pay.description && <div className="text-xs text-ink-4 italic">{pay.description}</div>}
                            </div>
                          ) : <span className="text-xs text-ink-5">—</span>}
                        </td>
                        <td className="py-2 px-3 text-right border border-ink-5 align-middle">
                          {desp ? (
                            <div>
                              <div className="text-sm font-semibold text-purple-700">{formatCurrency(desp.totalPrice)}</div>
                              <div className="text-xs text-ink-4 mt-0.5 font-mono">{desp.invoiceNumber}</div>
                              <div className="text-xs text-ink-4">{formatDate(desp.date)} · {desp.itemCount} item(s)</div>
                            </div>
                          ) : <span className="text-xs text-ink-5">—</span>}
                        </td>
                        {isFirst && (
                          <td rowSpan={numRows} className="py-2.5 px-3 text-right border border-ink-5 align-middle bg-ink-5/10">
                            <div className={`text-base font-bold ${balColor}`}>{formatCurrency(Math.abs(c.balance))}</div>
                            <div className={`text-xs uppercase tracking-wide mt-0.5 ${balColor}`}>{balLabel}</div>
                          </td>
                        )}
                      </tr>
                    );
                  });
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-p/40" style={{ background: 'var(--p-pale)' }}>
                  <td colSpan={3} className="py-2.5 px-3 text-sm font-bold text-ink text-right border border-ink-5">TOTALS</td>
                  <td className="py-2.5 px-3 text-right text-sm font-bold text-blue-700 border border-ink-5">{formatCurrency(totalContractsValue)}</td>
                  <td className="py-2.5 px-3 text-right text-sm font-bold text-green-700 border border-ink-5">{formatCurrency(totalPayments)}</td>
                  <td className="py-2.5 px-3 text-right text-sm font-bold text-purple-700 border border-ink-5">{formatCurrency(totalDespatched)}</td>
                  <td className={`py-2.5 px-3 text-right text-sm font-bold border border-ink-5 ${outstanding > 0 ? 'text-red-600' : 'text-green-700'}`}>
                    {formatCurrency(Math.abs(outstanding))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {contractPage && <Pagination {...contractPage} onPage={p => onPage('contracts', p)} />}

        <div className="mt-4 text-xs text-ink-4 text-center">
          Statement generated on {new Date().toLocaleString('en-PK', { dateStyle: 'medium', timeStyle: 'short' })}
          {' — '}Balance = Contract Value − Approved Credits − Payments
        </div>

        {/* ── Direct Sales Section ── */}
        {saleCount > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#7E22CE' }}>Direct Sales</span>
              <div className="flex-1 h-px" style={{ background: '#E9D5FF' }} />
            </div>

            {/* DS summary boxes */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="border border-purple-100 bg-purple-50 rounded-sm p-3 text-center" style={{ borderRadius: 'var(--r-xs)' }}>
                <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-1">Direct Sales Value</div>
                <div className="text-lg font-bold text-purple-700">{formatCurrency(directSalesValue)}</div>
                <div className="text-xs text-purple-500 mt-0.5">{saleCount} sale(s)</div>
              </div>
              <div className="border border-green-100 bg-green-50 rounded-sm p-3 text-center" style={{ borderRadius: 'var(--r-xs)' }}>
                <div className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">Paid (net)</div>
                <div className="text-lg font-bold text-green-700">{formatCurrency(directSalesPaid)}</div>
              </div>
              <div className={`border rounded-sm p-3 text-center ${directSalesOutstanding > 0 ? 'border-red-100 bg-red-50' : 'border-green-100 bg-green-50'}`} style={{ borderRadius: 'var(--r-xs)' }}>
                <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${directSalesOutstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>Outstanding</div>
                <div className={`text-lg font-bold ${directSalesOutstanding > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatCurrency(Math.abs(directSalesOutstanding))}</div>
              </div>
            </div>

            <div className="border border-ink-5 rounded-sm overflow-hidden" style={{ borderRadius: 'var(--r-xs)' }}>
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ background: '#FAF5FF' }}>
                    <th className="py-2 px-3 text-center text-xs font-semibold text-ink-3 w-10 border border-ink-5">#</th>
                    <th className="py-2 px-3 text-left text-xs font-semibold text-ink-3 border border-ink-5">Sale #</th>
                    <th className="py-2 px-3 text-left text-xs font-semibold text-ink-3 border border-ink-5">Date / Activity</th>
                    <th className="py-2 px-3 text-left text-xs font-semibold text-ink-3 border border-ink-5">Payment / Credit</th>
                    <th className="py-2 px-3 text-right text-xs font-semibold text-purple-700 border border-ink-5">Sale Total</th>
                    <th className="py-2 px-3 text-right text-xs font-semibold text-green-700 border border-ink-5">Paid</th>
                    <th className="py-2 px-3 text-right text-xs font-semibold text-red-600 border border-ink-5">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {directSales.map((d, idx) => (
                    <tr key={d.saleNumber} className={`${d.overdue ? 'bg-red-50' : idx % 2 === 0 ? 'bg-white' : 'bg-ink-5/5'}`}>
                      <td className="py-2 px-3 text-center text-sm font-bold text-ink-3 border border-ink-5">{saleOffset + idx + 1}</td>
                      <td className="py-2 px-3 border border-ink-5"><span className="font-mono text-xs font-bold" style={{ color: '#7E22CE' }}>{d.saleNumber}</span></td>
                      <td className="py-2 px-3 text-xs text-ink border border-ink-5">
                        {formatDate(d.date)} · {d.itemCount} item(s)
                        {(d.returnsCount > 0 || d.exchangesCount > 0) && (
                          <div className="text-[10px] font-semibold mt-0.5" style={{ color: '#7E22CE' }}>
                            {d.returnsCount > 0 && `${d.returnsCount} return(s)`}{d.returnsCount > 0 && d.exchangesCount > 0 && ' · '}{d.exchangesCount > 0 && `${d.exchangesCount} exchange(s)`}
                          </div>
                        )}
                        {(d.discountTotal ?? 0) > 0.01 && (
                          <div className="text-[10px] font-semibold mt-0.5 text-green-700">Discount: {formatCurrency(d.discountTotal!)}</div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs border border-ink-5">
                        {d.outstanding <= 0.01
                          ? <span className="text-green-700 font-semibold">Paid</span>
                          : d.isCredit
                            ? <span className={`font-semibold ${d.overdue ? 'text-red-600' : 'text-amber-600'}`}>Credit{d.creditDueDate ? ` · due ${formatDate(d.creditDueDate)}` : ''}{d.overdue ? ' · OVERDUE' : ''}</span>
                            : <span className="text-ink-4">Partial</span>}
                      </td>
                      <td className="py-2 px-3 text-right text-sm font-bold text-ink-2 border border-ink-5">{formatCurrency(d.grandTotal)}</td>
                      <td className="py-2 px-3 text-right text-sm font-bold text-green-700 border border-ink-5">{formatCurrency(d.totalPaid)}</td>
                      <td className={`py-2 px-3 text-right text-sm font-bold border border-ink-5 ${d.outstanding > 0.01 ? 'text-red-600' : 'text-green-700'}`}>{formatCurrency(d.outstanding)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2" style={{ background: '#FAF5FF', borderTopColor: '#7E22CE' }}>
                    <td colSpan={4} className="py-2.5 px-3 text-right text-sm font-bold text-ink border border-ink-5">DIRECT SALES TOTALS</td>
                    <td className="py-2.5 px-3 text-right text-sm font-bold text-purple-700 border border-ink-5">{formatCurrency(directSalesValue)}</td>
                    <td className="py-2.5 px-3 text-right text-sm font-bold text-green-700 border border-ink-5">{formatCurrency(directSalesPaid)}</td>
                    <td className={`py-2.5 px-3 text-right text-sm font-bold border border-ink-5 ${directSalesOutstanding > 0 ? 'text-red-600' : 'text-green-700'}`}>{formatCurrency(Math.abs(directSalesOutstanding))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {salePage && <Pagination {...salePage} onPage={p => onPage('directSales', p)} />}
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ClientStatementPage() {
  const [searchInput, setSearchInput] = useState('');
  const [searching,   setSearching]   = useState(false);
  const [clients,     setClients]     = useState<Client[]>([]);
  const [searched,    setSearched]    = useState(false);
  const [client,      setClient]      = useState<Client | null>(null);
  const [statement,   setStatement]   = useState<ClientStatement | null>(null);
  const [loadingSt,   setLoadingSt]   = useState(false);
  const [fromDate,    setFromDate]    = useState('');
  const [toDate,      setToDate]      = useState('');

  const handleSearch = useCallback(async () => {
    const q = searchInput.trim();
    if (!q) { toast.error('Enter client name or company'); return; }
    setSearching(true);
    setClients([]);
    setSearched(false);
    setClient(null);
    setStatement(null);
    try {
      const res = await clientAPI.getAll({ search: q, limit: 10 });
      setClients(res.data.data.clients);
      setSearched(true);
      if (!res.data.data.clients.length) toast.error('No clients found');
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  }, [searchInput]);

  const loadStatement = async (c: Client, page = 1, directPage = 1) => {
    setLoadingSt(true);
    try {
      const params: { from?: string; to?: string; page: number; directPage: number } = { page, directPage };
      if (fromDate) params.from = fromDate;
      if (toDate)   params.to   = toDate;
      const res = await clientAPI.getStatement(c._id, params);
      setStatement(res.data.data);
    } catch { toast.error('Failed to load statement'); }
    finally { setLoadingSt(false); }
  };

  const handleSelectClient = async (c: Client) => {
    setClients([]);
    setSearched(false);
    setClient(c);
    setStatement(null);
    await loadStatement(c);
  };

  const handleLoadStatement = async () => {
    if (!client) return;
    setStatement(null);
    await loadStatement(client);
  };

  const handleStatementPage = (list: 'contracts' | 'directSales', page: number) => {
    if (!client || !statement) return;
    const current = { contracts: statement.contractsPagination?.page ?? 1, directSales: statement.directSalesPagination?.page ?? 1, [list]: page };
    loadStatement(client, current.contracts, current.directSales);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div>
      <PageHeader
        title="Client Statement"
        subtitle="View full debit / credit ledger for any client"
      />

      {/* Search Bar */}
      <Card className="p-5 mb-4">
        <div className="flex gap-3 items-end">
          <Field label="Search Client" className="flex-1">
            <div className="relative">
              <MdSearch size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
              <Input
                placeholder="Client name or company..."
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-9"
              />
            </div>
          </Field>
          <Button onClick={handleSearch} loading={searching} icon={<MdSearch size={16} />}>
            Search Client
          </Button>
        </div>

        {/* Date Filter (shown after client selected) */}
        {client && (
          <div className="mt-4 pt-4 border-t border-ink-5">
            <div className="flex gap-3 items-end flex-wrap">
              <Field label="From Date" className="w-40">
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFromDate(e.target.value)}
                />
              </Field>
              <Field label="To Date" className="w-40">
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setToDate(e.target.value)}
                />
              </Field>
              <Button variant="secondary" onClick={handleLoadStatement} loading={loadingSt}>
                Apply Filter
              </Button>
              {(fromDate || toDate) && (
                <Button variant="secondary" onClick={() => { setFromDate(''); setToDate(''); }}>
                  Clear Dates
                </Button>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Searching */}
      {searching && (
        <div className="flex items-center justify-center py-16 gap-3">
          <Spinner size={24} />
          <span className="text-ink-3 text-sm">Searching clients...</span>
        </div>
      )}

      {/* Client Search Results */}
      {!searching && searched && clients.length > 0 && (
        <Card className="overflow-hidden mb-4">
          <div className="px-4 py-3 border-b border-ink-5" style={{ background: 'var(--p-pale)' }}>
            <span className="text-xs font-semibold uppercase tracking-widest text-ink-3">
              {clients.length} client{clients.length !== 1 ? 's' : ''} found — click to view statement
            </span>
          </div>
          <div className="divide-y divide-ink-5">
            {clients.map((c) => (
              <button
                key={c._id}
                onClick={() => handleSelectClient(c)}
                className="w-full text-left px-4 py-3.5 hover:bg-p-pale transition-colors flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                    style={{ background: 'var(--p)' }}>
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-ink">{c.name}</div>
                    {c.companyName && <div className="text-xs text-ink-4">{c.companyName}</div>}
                  </div>
                </div>
                <div className="text-right">
                  {c.city && <div className="text-xs text-ink-4">{c.city}</div>}
                  {c.contactNumber && <div className="text-xs text-ink-4">{c.contactNumber}</div>}
                </div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Loading Statement */}
      {loadingSt && (
        <div className="flex items-center justify-center py-16 gap-3">
          <Spinner size={24} />
          <span className="text-ink-3 text-sm">Loading statement...</span>
        </div>
      )}

      {/* Statement */}
      {!loadingSt && client && statement && (
        <StatementTable statement={statement} client={client} fromDate={fromDate} toDate={toDate} onPage={handleStatementPage} />
      )}

      {/* Initial State */}
      {!searching && !searched && !client && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: 'var(--p-pale)' }}>
            <MdAccountBalance size={32} className="text-p-deep opacity-40" />
          </div>
          <div className="text-base font-semibold text-ink-3">Client Ledger</div>
          <div className="text-sm text-ink-4 mt-1">Search a client above to view their account statement</div>
        </div>
      )}
    </div>
  );
}
